import { useMemo } from 'react';
import { PhoneCallsQueries } from '@frontend/api-phone-calls';
import { useTranslation } from '@frontend/i18n';
import { useHasPhoneSystemAccess } from '@frontend/phone-config';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const useCallsNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasPhoneSystemAccess = useHasPhoneSystemAccess({ locationIds: selectedLocationIds });
  const hasTeamPresenceAccess = getFeatureFlagValue('team-presence');
  const voicemailsUnreadCount = PhoneCallsQueries.useVoicemailsUnreadCount();

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          label: t('Call History'),
          altSearchLabel: t('Recent Calls'),
          relativePath: '/call-history',
          trackingId: 'nav-calls-recent-calls',
          description:
            'A log of inbound and outbound calls, including caller details, call recordings, and timestamps.',
        },
        {
          count: voicemailsUnreadCount,
          label: t('Voicemails'),
          relativePath: '/voicemails',
          trackingId: 'nav-calls-voicemails',
          description: 'Displays recorded voicemail messages. Users can listen, mark as read, tag, or delete messages.',
        },
        {
          label: t('Call Queue Data'),
          relativePath: '/call-queue-stats',
          trackingId: 'nav-calls-call-queue-data',
          description:
            'Provides call queue metrics like hold times, number of callers in queue, and service performance statistics.',
        },
        {
          label: t('Team Presence'),
          relativePath: '/team-presence',
          trackingId: 'nav-calls-team-presence',
          description: 'Monitor user and device presence.',
          show: hasTeamPresenceAccess,
        },
      ]),
    [t, voicemailsUnreadCount, hasTeamPresenceAccess]
  );

  return useMemo(
    () =>
      createNavItem('calls', {
        hasCount: !!voicemailsUnreadCount,
        iconName: 'phone',
        show: hasPhoneSystemAccess,
        root: '/calls',
        label: t('Calls'),
        subNavItems,
        settings: { path: '/phone/main', label: '' },
      }),
    [t, voicemailsUnreadCount, hasPhoneSystemAccess, subNavItems]
  );
};
